import axios from 'axios';
import { getAccessToken } from '../auth/session';

const API_BASE_URL = 'https://d120ovw29hasnn.cloudfront.net/user';

// Single API request helper. Pulls the access token from Amplify (which
// handles storage and refresh for both Cognito-native and Google-federated
// sessions). On a 401 we force a token refresh and retry exactly once;
// if that still fails the session is genuinely dead.
const apiRequest = async (endpoint, method = 'GET', body = null) => {
  const send = async (forceRefresh) => {
    const token = await getAccessToken({ forceRefresh });
    if (!token) {
      throw new Error('Not authenticated');
    }
    return axios({
      method,
      url: `${API_BASE_URL}${endpoint}`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
      ...(body && { data: body }),
    });
  };

  try {
    const response = await send(false);
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      try {
        const retry = await send(true);
        return retry.data;
      } catch (retryError) {
        if (retryError.response?.status === 404) return { exists: false };
        throw retryError;
      }
    }
    if (error.response?.status === 404) return { exists: false };
    throw error;
  }
};

const userHandler = {
  async upsertUser(email, userName) {
    return apiRequest('/users/upsert', 'POST', { email, userName });
  },

  async getUser(email) {
    try {
      return await apiRequest(`/users/${encodeURIComponent(email)}`);
    } catch (error) {
      if (error.response?.status === 404) return { exists: false };
      throw error;
    }
  },

  async updateUser(email, updateData, suppressNotification = false) {
    try {
      const response = await apiRequest(
        `/users/${encodeURIComponent(email)}`,
        'PUT',
        updateData
      );

      if (response.success) {
        const updatedAttributes = Object.keys(updateData).join(', ');
        return {
          success: true,
          user: response.user,
          message: suppressNotification ? null : `${updatedAttributes} has been updated`,
          type: suppressNotification ? null : 'success',
        };
      }
      return {
        success: false,
        error: response.error || 'Failed to update user',
        type: suppressNotification ? null : 'error',
      };
    } catch (error) {
      console.error('Error updating user:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'An error occurred while updating the user.',
        type: suppressNotification ? null : 'error',
      };
    }
  },

  async getTiers() {
    return apiRequest('/tiers');
  },

  async createCheckoutSession(email, priceId) {
    return apiRequest('/create-checkout-session', 'POST', { email, priceId });
  },

  // Public endpoint (token is the unsubscribe JWT itself, not a Cognito session).
  async verifyUnsubscribeToken(token) {
    const response = await axios.post(
      `${API_BASE_URL}/verify-unsubscribe-token`,
      { token },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },

  async getChatMessages(userEmail) {
    return apiRequest(`/chat/messages?userEmail=${encodeURIComponent(userEmail)}`);
  },

  async sendChatMessage(userEmail, message) {
    return apiRequest('/chat/send', 'POST', { userEmail, message });
  },
};

export default userHandler;
